import { useLocation, useNavigate } from "react-router-dom";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apis from "../services/apis";
import { useAuth } from "./AuthContext";
import apiBolsa from "../services/apiBolsa";
import { apiPayment } from "../services/apiPayment";
import rendimentosBackend from "../services/rendimentos";
import { Broker, brokers, Page } from "../constants/brokers";
import { LoadingSession } from "../components/LoadingSession";
import { LoadingIntegration } from "../components/LoadingIntegration";

interface Integration {
  key: string;
  integrated: boolean;
  lastUpdate: string;
  providerExists: boolean;
  plans?: { name: string; status: string };
  secret?: string;
  userId?: string;
}

const defaultIntegration: Integration = {
  key: "",
  lastUpdate: "",
  integrated: false,
  providerExists: false,
};

interface IBrokerContext {
  currentBroker: Broker;
  currentPage: Page;
  integration: Integration;
  integrations: Integration[];
  brokersAbledPages: Page[];
  currentBrokerAbledPages: Page[];
  initIntegration: boolean;
  loadingIntegrationStatus: boolean;

  handleIntegrationInit: () => void;
  getIntegrationStatus: (options: {
    token?: string;
    firstTime?: boolean;
    hideLoading?: boolean;
  }) => void;
  handleProviderExists: (value: boolean) => void;
}

export const BrokerContext = createContext<any>({});

export const BrokerProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();
  const { hostname } = window.location;
  const { pathname } = useLocation();
  const { user, loadingSession } = useAuth();
  const [initIntegration, setInitIntegration] = useState(false);
  const [integration, setIntegration] = useState(defaultIntegration);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loadingIntegrationStatus, setLoadingIntegrationStatus] =
    useState(false);

  const interval = useRef<ReturnType<typeof setInterval>>();

  const brokerList = useMemo(
    () => brokers.filter((broker) => !broker.disabled),
    []
  );

  const dnsBroker = useMemo(
    () => brokerList.find((broker) => hostname.includes(broker.dns)),
    [brokerList, hostname]
  );

  const currentBroker = useMemo(
    () =>
      dnsBroker ||
      brokerList.find((broker) => pathname.includes(broker.path)) ||
      brokerList[0],
    [pathname, brokerList, dnsBroker]
  );

  const currentPage = useMemo(() => {
    const current = currentBroker?.pages.find((page) =>
      pathname.includes(page.path)
    );
    if (current?.api) {
      current.api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${localStorage.getItem("@VeloTax:token")}`;
    }
    return current;
  }, [pathname, currentBroker?.pages]);

  const getIntegrationStatus = useCallback(
    ({
      token = user.token,
      hideLoading,
      firstTime,
    }: {
      token?: string;
      firstTime?: boolean;
      hideLoading?: boolean;
    }) => {
      if (token && currentBroker) {
        const pathnameWindowArray = String(window.location.href || "").split(
          "/"
        );
        const pathnameWindow =
          pathnameWindowArray?.[pathnameWindowArray.length - 1] || "";

        !hideLoading && setLoadingIntegrationStatus(true);
        (pathnameWindow === "crypto-integrations" ? apis : apiBolsa)
          .get(`providers/integration-status`)
          .then((response) => {
            if (response.data) {
              if (Array.isArray(response.data)) {
                setIntegrations(response.data);
              } else {
                const { key, integrated, lastUpdate, providerExists, plans } =
                  response.data;
                setIntegration({
                  key,
                  integrated,
                  lastUpdate,
                  providerExists,
                  plans,
                });
                if (firstTime) {
                  if (providerExists) {
                    navigate(
                      currentBroker.defaultProviderExistsPath ??
                      "/velotax/bolsa-darf"
                    );
                  } else {
                    navigate(
                      currentBroker.defaultPath ?? "/velotax/bolsa-integration"
                    );
                  }
                }
              }
            } else {
              setIntegrations([]);
              setIntegration(defaultIntegration);
            }
          })
          .catch(() => {
            setIntegration(defaultIntegration);
          })
          .finally(() => !hideLoading && setLoadingIntegrationStatus(false));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentBroker, user.token]
  );

  const handleIntegrationInit = useCallback(() => {
    setInitIntegration(true);
    setIntegration((integration) => ({ ...integration, integrated: false }));
  }, []);

  const handleProviderExists = useCallback((providerExists: boolean) => {
    setIntegration((integration) => ({ ...integration, providerExists }));
  }, []);

  const filterPage = (page: Page) =>
    !page.disabled &&
    (integration.providerExists || !page.disabledIfNotIntegrated) &&
    (!integration.providerExists || !page.disabledIfIntegrated);

  const currentBrokerAbledPages = currentBroker?.pages.filter(filterPage);

  const brokersAbledPages = brokers
    .filter((broker) => !broker.disabled)
    .reduce((acc, cur) => [...acc, ...cur.pages], [] as Page[])
    .filter(filterPage);

  useEffect(() => {
    if (currentBroker?.html.favicon) {
      const link = (document.querySelector("link[rel~='icon']") ||
        document.createElement("link")) as HTMLLinkElement;
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
      link.href = currentBroker?.html.favicon;
    }
    if (currentBroker?.html.title) {
      const title = (document.querySelector("head title") ||
        document.createElement("title")) as HTMLTitleElement;
      document.getElementsByTagName("head")[0].appendChild(title);
      title.innerHTML = currentBroker.html.title;
    }
  }, [currentBroker]);

  useEffect(() => {
    getIntegrationStatus({ token: user.token });
  }, [getIntegrationStatus, user.token]);

  useEffect(() => {
    if (integration.integrated && interval.current) {
      clearInterval(interval.current);
      setInitIntegration(false);
      if (
        initIntegration &&
        pathname === `/${currentBroker.path}/bolsa-integration`
      ) {
        navigate(`/${currentBroker.path}/bolsa-insert-manual`);
      }
    }
  }, [
    integration.integrated,
    pathname,
    currentBroker,
    navigate,
    initIntegration,
  ]);

  useEffect(() => {
    if (initIntegration) {
      interval.current = setInterval(
        () => getIntegrationStatus({ token: user.token }),
        5000
      );
    }
  }, [getIntegrationStatus, user.token, initIntegration]);

  apis.defaults.headers.common["x-api-provider"] = 'crypto-mynt';
  // apis.defaults.headers.common["x-api-provider"] = currentBroker?.id || "";
  apiPayment.defaults.headers.common["x-api-provider"] = currentBroker?.id || "";
  rendimentosBackend.defaults.headers.common["x-api-provider"] =
    "velotax-rendimentos";
  return (
    <BrokerContext.Provider
      value={{
        currentBroker,
        currentPage,
        integration,
        integrations,
        handleIntegrationInit,
        handleProviderExists,
        getIntegrationStatus,
        brokersAbledPages: dnsBroker
          ? currentBrokerAbledPages
          : brokersAbledPages,
        initIntegration,
        currentBrokerAbledPages,
        loadingIntegrationStatus,
      }}
    >
      {children}
      {initIntegration && <LoadingIntegration />}
      {!initIntegration && (loadingSession || loadingIntegrationStatus) && (
        <LoadingSession />
      )}
    </BrokerContext.Provider>
  );
};

export const useBroker = (): IBrokerContext => useContext(BrokerContext);
